import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekOfYear from 'dayjs/plugin/weekOfYear';

import { CalendarConfig } from 'src/services/config';

import { FrequencyType } from '../../types/common';

// 加载插件
dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);

type DateInput = string | number | Date | dayjs.Dayjs;

/**
 * 根据频率类型格式化日期
 */
export function formatDateByFrequency(date: DateInput, frequencyType: FrequencyType | string): string {
  if (!date) return '';

  // 转换为dayjs对象
  const dayjsObj = dayjs.isDayjs(date) ? date : dayjs(date);

  switch (frequencyType) {
    case FrequencyType.Yearly:
      return `${dayjsObj.format('YYYY')}年`;
    case FrequencyType.Quarterly: {
      const quarter = Math.floor(dayjsObj.month() / 3) + 1;
      return `${dayjsObj.format('YYYY')}年第${quarter}季度`;
    }
    case FrequencyType.Monthly:
      return `${dayjsObj.format('YYYY')}年${dayjsObj.format('MM')}月`;
    case FrequencyType.Weekly: {
      const month = dayjsObj.format('MM');
      
      // 计算该周在所属月份中的周数
      const monthStart = dayjsObj.startOf('month');
      const monthFirstWeek = monthStart.isoWeek();
      const currentWeek = dayjsObj.isoWeek();
      
      // 处理跨年的情况
      let weekInMonth;
      if (currentWeek >= monthFirstWeek) {
        weekInMonth = currentWeek - monthFirstWeek + 1;
      } else {
        // 跨年情况：当前周数小于月初周数（例如1月包含去年最后一周）
        const weeksInPrevYear = dayjs(`${dayjsObj.year() - 1}-12-31`).isoWeek();
        weekInMonth = currentWeek + weeksInPrevYear - monthFirstWeek + 1;
      }
      
      return `${dayjsObj.format('YYYY')}年${month}月第${weekInMonth}周`;
    }
    default:
      return dayjsObj.format('YYYY-MM-DD');
  }
}

/**
 * 根据年份和序列获取日期对象
 */
export function getDateFromYearAndSequence(startYear: number, sequence: number, frequency: FrequencyType): dayjs.Dayjs {
  switch (frequency) {
    case FrequencyType.Yearly:
      return dayjs(`${startYear}-01-01`);
    case FrequencyType.Quarterly: {
      const month = (sequence - 1) * 3;
      return dayjs(`${startYear}-${month + 1}-01`);
    }
    case FrequencyType.Monthly:
      return dayjs(`${startYear}-${sequence}-01`);
    case FrequencyType.Weekly:
      return dayjs(`${startYear}-01-01`).isoWeek(sequence);
    default:
      return dayjs(`${startYear}-01-01`);
  }
}

/**
 * 从日期对象获取年份和序列
 */
export function getYearAndSequence(date: dayjs.Dayjs, frequency: FrequencyType): [number, number] {
  switch (frequency) {
    case FrequencyType.Yearly:
      return [date.year(), 1];
    case FrequencyType.Quarterly:
      return [date.year(), Math.ceil((date.month() + 1) / 3)];
    case FrequencyType.Monthly:
      return [date.year(), date.month() + 1];
    case FrequencyType.Weekly:
      return [date.year(), date.isoWeek()];
    default:
      return [date.year(), 1];
  }
}

/**
 * 获取上一个周期的年份和序列
 */
export function getPreviousOccurrence(
  startYear: number,
  sequence: number,
  frequency: FrequencyType
): [number, number] | null {
  if (startYear <= 0 || sequence <= 0) return null;

  switch (frequency) {
    case FrequencyType.Yearly:
      return [startYear - 1, 1];
    case FrequencyType.Quarterly:
      return sequence > 1 ? [startYear, sequence - 1] : [startYear - 1, 4];
    case FrequencyType.Monthly:
      return sequence > 1 ? [startYear, sequence - 1] : [startYear - 1, 12];
    case FrequencyType.Weekly: {
      if (sequence > 1) return [startYear, sequence - 1];
      const lastWeekOfPrevYear =
        Math.ceil(dayjs(`${startYear - 1}-12-31`).diff(dayjs(`${startYear - 1}-01-01`), 'week')) + 1;
      return [startYear - 1, lastWeekOfPrevYear];
    }
    default:
      return null;
  }
}

/**
 * 根据频率类型和序列获取日期范围
 */
export function getDateRangeByFrequency(
  startYear: number, 
  sequence: number, 
  frequencyType: FrequencyType | string
): { startDate: dayjs.Dayjs; endDate: dayjs.Dayjs } {
  let startDate: dayjs.Dayjs;
  let endDate: dayjs.Dayjs;

  switch (frequencyType) {
    case FrequencyType.Yearly:
      startDate = dayjs(`${startYear}-01-01`);
      endDate = dayjs(`${startYear}-12-31`);
      break;
    case FrequencyType.Quarterly: {
      const startMonth = (sequence - 1) * 3;
      startDate = dayjs(`${startYear}-${startMonth + 1}-01`);
      endDate = startDate.add(3, 'month').subtract(1, 'day');
      break;
    }
    case FrequencyType.Monthly:
      startDate = dayjs(`${startYear}-${sequence}-01`);
      endDate = startDate.endOf('month');
      break;
    case FrequencyType.Weekly:
      startDate = dayjs().year(startYear).isoWeek(sequence).startOf('isoWeek');
      endDate = startDate.endOf('isoWeek');
      break;
    default:
      startDate = dayjs(`${startYear}-01-01`);
      endDate = dayjs(`${startYear}-12-31`);
  }

  return { startDate, endDate };
}

/**
 * 根据频率类型和序列获取日期范围（ISO字符串格式）
 */
export function getDateRangeStringsByFrequency(
  startYear: number, 
  sequence: number, 
  frequencyType: FrequencyType | string
): { startDate: string; endDate: string } {
  const { startDate, endDate } = getDateRangeByFrequency(startYear, sequence, frequencyType);
  return {
    startDate: startDate.toISOString().replace('Z', ''),
    endDate: endDate.toISOString().replace('Z', '')
  };
}

/**
 * 计算一个日期在指定频率下的序列号
 */
export function getSequenceByDate(date: DateInput, frequencyType: FrequencyType, calendarConfig: CalendarConfig): number {
  const dayjsObj = dayjs.isDayjs(date) ? date : dayjs(date);

  switch (frequencyType) {
    case FrequencyType.Yearly:
      return 1;
    case FrequencyType.Quarterly:
      return dayjsObj.quarter();
    case FrequencyType.Monthly:
      return dayjsObj.month() + 1;
    case FrequencyType.Weekly:
      if(calendarConfig[dayjsObj.year()]) {
        return calendarConfig[dayjsObj.year()].find((n)=> dayjsObj.isSameOrAfter(dayjs(n.startDate)) && dayjsObj.isSameOrBefore(dayjs(n.endDate)))?.sequence ?? -1
    }
    return -1
    default:
      return -1;
  }
}

export const getWeelySequenceByDate = (calendarConfig: CalendarConfig, date: dayjs.Dayjs )=> {
    if(calendarConfig[date.year()]) {
        return calendarConfig[date.year()].find((n)=> date.isSameOrAfter(dayjs(n.startDate)) && date.isSameOrBefore(dayjs(n.endDate)))?.weeklySequence ?? -1
    }
    return -1
}

/**
 * 获取指定年份某个频率类型的有效序列范围
 */
export function getSequenceRangeByYear(year: number, frequencyType: FrequencyType | string): [number, number] {
  switch (frequencyType) {
    case FrequencyType.Yearly:
      return [1, 1];
    case FrequencyType.Quarterly:
      return [1, 4];
    case FrequencyType.Monthly:
      return [1, 12];
    case FrequencyType.Weekly: {
      const lastDay = dayjs(`${year}-12-31`);
      const lastWeek = lastDay.isoWeek();
      const maxWeek = lastWeek === 1 ? 53 : lastWeek;
      return [1, maxWeek];
    }
    default:
      return [0, 0];
  }
}

// 为了兼容性，添加映射对象
export const getDateFromYearAndSequenceMap: Record<FrequencyType, (startYear: number, sequence: number) => dayjs.Dayjs> = {
  [FrequencyType.Yearly]: (startYear, sequence) => getDateFromYearAndSequence(startYear, sequence, FrequencyType.Yearly),
  [FrequencyType.Quarterly]: (startYear, sequence) => getDateFromYearAndSequence(startYear, sequence, FrequencyType.Quarterly),
  [FrequencyType.Monthly]: (startYear, sequence) => getDateFromYearAndSequence(startYear, sequence, FrequencyType.Monthly),
  [FrequencyType.Weekly]: (startYear, sequence) => getDateFromYearAndSequence(startYear, sequence, FrequencyType.Weekly)
};

// 为了兼容性，添加映射对象
export const getYearAndSequenceMap: Record<FrequencyType, (date: dayjs.Dayjs) => [number, number]> = {
  [FrequencyType.Yearly]: (date) => getYearAndSequence(date, FrequencyType.Yearly),
  [FrequencyType.Quarterly]: (date) => getYearAndSequence(date, FrequencyType.Quarterly),
  [FrequencyType.Monthly]: (date) => getYearAndSequence(date, FrequencyType.Monthly),
  [FrequencyType.Weekly]: (date) => getYearAndSequence(date, FrequencyType.Weekly)
};